import {useCallback, useEffect, useState} from 'react';

import {useAnnotationModeStore} from './AnnotationModeStore';
import {
  clearRegionHighlights,
  hasRegionHighlights,
  setRegionHighlights
} from './annotationPaintCoordinator';
import type {Annotation, AnnotationAnchor} from './annotationTypes';
import {
  captureSelectionAnchor,
  findAnnotationAtPoint,
  hasUntypesetMath,
  isHighlightApiSupported,
  resolveAnnotationRanges
} from './annotationUtils';
import {useTextProjection} from './useTextProjection';

const EMPTY_ANNOTATIONS: Annotation[] = [];

/**
 * `containerRef` is a callback ref so the effects re-run if the element
 * attaches late (e.g. the feature flag resolving after first render).
 */
export function useAnnotations(
  regionId: string,
  lang: string | null = null
): {
  captureAnnotation: () => AnnotationAnchor | null;
  getAnnotationAtPoint: (x: number, y: number) => AnnotationAnchor | null;
  containerRef: (element: HTMLElement | null) => void;
} {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const annotations = useAnnotationModeStore(
    (state) => state.annotationsByRegionId[regionId] ?? EMPTY_ANNOTATIONS
  );
  const addAnnotation = useAnnotationModeStore((state) => state.addAnnotation);
  const setRegionHasAnnotatableContent = useAnnotationModeStore(
    (state) => state.setRegionHasAnnotatableContent
  );

  const getProjection = useTextProjection(container, {
    onMutate: () => clearRegionHighlights(regionId),
    // Deferred closure: `repaint` is declared below; the hook reads handlers
    // through a ref, so the latest one always fires.
    onSettle: () => repaint()
  });

  const repaint = useCallback(() => {
    if (!container || !isHighlightApiSupported()) {
      return;
    }
    const projection = annotations.length > 0 ? getProjection() : null;
    setRegionHighlights(
      regionId,
      projection ? resolveAnnotationRanges(projection, annotations) : new Map()
    );
  }, [container, regionId, annotations, getProjection]);

  const captureAnnotation = useCallback((): AnnotationAnchor | null => {
    const captured = captureSelectionAnchor(getProjection, {regionId, lang});
    if (!captured) {
      return null;
    }
    return {rect: captured.rect, id: addAnnotation(captured.anchor)};
  }, [addAnnotation, getProjection, regionId, lang]);

  const getAnnotationAtPoint = useCallback(
    (x: number, y: number): AnnotationAnchor | null => {
      const projection = annotations.length > 0 ? getProjection() : null;
      if (!container || !projection) {
        return null;
      }
      return findAnnotationAtPoint(container, projection, x, y, annotations);
    },
    [container, getProjection, annotations]
  );

  useEffect(() => {
    // With nothing painted yet, MathJax rewrites are waited out (no flash
    // against stale DOM); once painted, annotation changes always resync.
    if (container && (hasRegionHighlights(regionId) || !hasUntypesetMath(container))) {
      repaint();
    }
  }, [container, regionId, repaint]);

  // The region's paint dies with its container (unmount, region switch, detach).
  useEffect(() => {
    if (!container) {
      return undefined;
    }
    return () => clearRegionHighlights(regionId);
  }, [container, regionId]);

  useEffect(() => {
    setRegionHasAnnotatableContent(regionId, Boolean(container?.textContent?.trim()));
    return () => setRegionHasAnnotatableContent(regionId, false);
  }, [regionId, container, setRegionHasAnnotatableContent]);

  return {captureAnnotation, getAnnotationAtPoint, containerRef: setContainer};
}
